import {
  buildConfigurationText,
  buildMeasureAccessor,
  DataPointsApiResponse,
  getLatestValue,
  getWarehousedDefinitionWithDayConfig,
  isTimelessWithWarehousingDisabled
} from '@tableau/metrics-engine';
import { StandardMetricCard } from '@tableau/metrics-shared-widgets-dom';
import React from 'react';
import { classes, style } from 'typestyle';
import _ from 'underscore';

import { telemetryConsumerWrapper } from '~commonInfra/telemetryEvents/contexts/TelemetryConsumerWrapper';
import { ContentClickedTelemetryContext } from '~commonInfra/telemetryEvents/contexts/TelemetryContexts';
import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { FavoriteToggle } from '~src/favorites/components/favoriteToggle/FavoriteToggle';
import { CardViewStyles } from '~src/spacesPlatform/components/card/CardViewStyles';
import { CardPopoverWidget, ContentCard, ContentCardProps, getContentUrlForItem } from '~src/spacesPlatform/components/card/ContentCard';
import { Thumbnail } from '~src/spacesPlatform/components/card/Thumbnail';

import { L10n } from '../../commonInfra/L10n/L10n';
import { ContentClickedEventHandler } from '../../commonInfra/telemetryEvents/types/ContentTelemetryTypes';
import { mouseUpFactory } from '../../commonInfra/telemetryEvents/util/EventHandlerFactories';
import { imgPath } from '../../commonInfra/utils/ImgUrl';
import { IExplorableItemTypeInfo } from '../../spacesPlatform/models/resourceInfoParts';
import { metricCardProps } from '..';
import { isMetricInfo, isUsageMetricInfo } from '../models/IMetricInfo';
import { ConnectedMetricTooltip } from './MetricTooltip';

interface MetricCardState {
  readonly metricData?: DataPointsApiResponse;
}

export interface MetricCardOwnProps extends ContentCardProps {
}

export interface MetricCardStateProps {
  readonly metricData: DataPointsApiResponse;
}

export type MetricCardProps = MetricCardOwnProps & MetricCardStateProps;

export const MetricDefaultImageUrl = imgPath('Spaces_Empty_Metrics.svg');

export class MetricCard extends ContentCard<MetricCardProps, MetricCardState> {
  public state: MetricCardState = {};

  private renderTimerHandle?: ReturnType<typeof setTimeout>;

  public render() {
    const { content, inRecentsSpace, addContentFavorite, removeContentFavorite } = this.props;
    const showLastAccessedAt = inRecentsSpace || this.props.showLastAccessedAt;

    return (
      <div className={CardViewStyles.Card}>
        {this.renderMetricCard()}
        <div className={CardViewStyles.ActionRow}>
          <span className={CardViewStyles.ActionRowName} title={content.name}>
            {L10n.elideIfLong(content.name)}
          </span>
          <span>
            <CardPopoverWidget
              content={content}
              onTriggerClick={this.handleShowPopover}
              popoverContent={getPopoverContent(this.props, this.state.metricData)}
              title={Messages.metricDetails()}
            />
          </span>
          { !isUsageMetricInfo(content) && <span>
            <FavoriteToggle isTabbable={true}
              isFavorite={content.favorite}
              onAddFavorite={() => addContentFavorite({ ...content, contentType: 'metric'})}
              onRemoveFavorite={() => removeContentFavorite({ ...content, contentType: 'metric'})} />
          </span>}
          { !isUsageMetricInfo(content) && this.renderActionsMenu() }
        </div>
        {
        showLastAccessedAt ? (
          <div className={CardViewStyles.CardLastAccessedAt}>
            {this.getLastAccessedAt()}
          </div>
        ) : (
          <div className={CardViewStyles.CardDescription}>
            {this.getDescription()}
          </div>
        )
        }
      </div>
    );
  }

  public componentDidUpdate(): void {
    this.delayAddingDataToState(this.state, this.props.content);
  }

  public componentDidMount(): void {
    this.delayAddingDataToState(this.state, this.props.content);
  }

  public componentWillUnmount() {
    if (this.renderTimerHandle) {
      clearTimeout(this.renderTimerHandle);
    }
  }

  private delayAddingDataToState(state: MetricCardState, content: IExplorableItemTypeInfo): void {
    if (isMetricInfo(content) && this.props.metricData !== state.metricData) {
    this.renderTimerHandle = setTimeout(() => this.setState({metricData: this.props.metricData}),
      Math.floor(Math.random() * 10));
    }
  }

  private renderMetricCard(): JSX.Element {
    if (this.haveMetricDataPoints()) {
      return renderMetricCardContent(this.state.metricData, this.props);
    } else {
      return (
        <Thumbnail
          defaultImageUrl={MetricDefaultImageUrl}
          hovered={this.props.hovered}
          selected={this.props.selected}
          navigateUrl={getContentUrlForItem(this.props.content, this.props.urls)}
          useBase64EncodedImage={true}
          allowDragAndDrop={this.props.allowDragAndDrop}
          aria-label={this.props.content.name}
        />
      );
    }
  }

  private haveMetricDataPoints(): boolean {
    return isMetricInfo(this.props.content) && this.state.metricData && !_.isUndefined(this.state.metricData.data);
  }
}

// exported for testing
export function getPopoverContent(props: MetricCardProps, metricData?: DataPointsApiResponse) {
  const cardProps = metricData ? metricCardProps(metricData) : undefined;
  const timestamp = cardProps ? cardProps.lastUpdatedTimestamp : undefined;
  const definition = cardProps ? getWarehousedDefinitionWithDayConfig(cardProps.definition) : undefined;
  const isTimelessWithoutWarehousing = metricData?.definition && isTimelessWithWarehousingDisabled(metricData.definition);
  const configurationText = metricData?.definition &&
    buildConfigurationText(getWarehousedDefinitionWithDayConfig(metricData.definition), isTimelessWithoutWarehousing);

  return <ConnectedMetricTooltip content={props.content}
                                  definition={definition}
                                  configurationText={configurationText}
                                  urls={props.urls}
                                  lastUpdatedTimestamp={timestamp}
                                  inProjectSpace={props.inProjectSpace}
                                  inPersonalSpace={props.inPersonalSpace}/>;
}

const metricThumbnailFocusStyle: string = style({
  $nest: {
    '& a.focus-visible': {
      ...CommonStyles.Focus.OutlineIndicator,
      display: 'block',
      height: '100%'
    }
  }
});

// This is a separate function so other card components can reuse the metric thumbnail
export function renderMetricCardContent(metricData: DataPointsApiResponse, contentCardProps: ContentCardProps): JSX.Element {
  const containerClasses = classes(CardViewStyles.ThumbnailPreloaded, metricThumbnailFocusStyle, {
    [CardViewStyles.CardSelectionHighlight]: contentCardProps.selected,
    [CardViewStyles.CardHoverHighlight]: contentCardProps.hovered && !contentCardProps.selected
  });

  const snapshotBannerProps = { isExpanded: contentCardProps.hovered || contentCardProps.selected };
  let metricCardProperties = { ...metricCardProps(metricData), snapshotBannerProps };
  if (contentCardProps.forDragPreview) {
    const measureAccessor = buildMeasureAccessor(metricCardProperties.definition);
    // when rendering the card just for a drag preview, don't show the sparkline because it makes the preview slower
    metricCardProperties.data = [getLatestValue(metricCardProperties.data, measureAccessor)];
  }

  return telemetryConsumerWrapper(ContentClickedTelemetryContext.Consumer, (sendOpenContentTelemetry: ContentClickedEventHandler): JSX.Element => {
    return (
      <div className={containerClasses}>
        <div className={CardViewStyles.SelectOverlay}></div>
        <a
          href={getContentUrlForItem(contentCardProps.content, contentCardProps.urls)}
          style={{ textDecoration: 'none' }}
          /**
           * If card reordering allowed, disable link dragging.
           * Otherwise, set `draggable` explicitly to undefined rather than true.
           * (When `draggable` is true, the browser-provided drag avatar looks too similar
           * to the reorder card feature's avatar, which may confuse users.)
           */
          draggable={contentCardProps.allowDragAndDrop ? false : undefined}
          onMouseUp={mouseUpFactory<HTMLAnchorElement>(sendOpenContentTelemetry)}
        >
          {<StandardMetricCard {...metricCardProperties} />}
        </a>
      </div>
    );
  });
}
